import { capitalize } from 'lodash';
import { z } from 'zod';

export type EntityRatingType = 'Rating' | 'ConversationRating';

export enum TurvoAuthMethod {
  PASSWORD = 'PASSWORD',
  CLIENT_CREDENTIALS = 'CLIENT_CREDENTIALS',
}

export const STANDARD_REFERENCE_TYPES = [
  'ORDER_REFERENCE',
  'PURCHASE_ORDER',
  'BILL_OF_LADING',
  'INVOICE',
  'SHIPMENT',
  'SALES_ORDER',
  'TRACKING',
  'DELIVERY',
  'CUSTOMER_REFERENCE',
  'LOAD',
  'CONTAINER',
  'MASTER_BILL_OF_LADING',
  'BOOKING',
  'SEAL',
  'PICKUP',
  'APPOINTMENT',
  'PRO',
  'CONSIGNEE_REFERENCE',
  'RMA_NUMBER',
  'PART_NUMBER',
  'JOB_NUMBER',
  'SERIAL_NUMBER',
  'CLAIM_NUMBER',
  'DELIVERY_ORDER',
  'PO_DUE_DATE',
  'QUOTE_NUMBER',
  'ITEM_REFERENCE',
  'OTHER',
] as const;
export const makeStandardReferenceTypeSchema = () =>
  z.enum(STANDARD_REFERENCE_TYPES);
const standardReferenceTypeSchema = makeStandardReferenceTypeSchema();
export type StandardReferenceType = z.infer<typeof standardReferenceTypeSchema>;

export const STANDARD_EQUIPMENT_LENGTHS = [
  12, 20, 24, 26, 28, 32, 36, 40, 42, 43, 45, 48, 53, 60, 65, 70,
] as const;
type StandardEquipmentLengthTuple = typeof STANDARD_EQUIPMENT_LENGTHS;
export type StandardFreightOrderEquipmentLength =
  StandardEquipmentLengthTuple[number];

export const STANDARD_EQUIPMENT_TYPES = [
  'AUTO_CARRIER',
  'BOAT_HAULING_TRAILER',
  'BOX_TRUCK',
  'CARGO_VAN',
  'CHASSIS_ONLY',
  'CHASSIS_ONLY_SUPER',
  'CHASSIS_ONLY_TRI_AXLE',
  'CONESTOGA',
  'CONTAINER',
  'CONTAINER_FLAT_RACK',
  'CONTAINER_HIGH_CUBE',
  'CONTAINER_INSULATED',
  'CONTAINER_OPEN_TOP',
  'CONTAINER_REFRIGERATED',
  'DOUBLE_DROP',
  'DRY_VAN',
  'DUMP_TRUCK',
  'FLATBED',
  'FLATBED_B_TRAIN',
  'FLATBED_EXTENDABLE',
  'FLATBED_MAXI',
  'FLATBED_OR_STEP_DECK',
  'HOPPER_BOTTOM',
  'HOT_SHOT',
  'LIVESTOCK',
  'LOW_BOY',
  'MULTI_AXLE_HEAVY_HAULER',
  'POWER_ONLY',
  'REEFER',
  'REMOVABLE_GOOSENECK',
  'REMOVABLE_GOOSENECK_EXTENDABLE',
  'SPRINTER_VAN',
  'STEP_DECK',
  'STEP_DECK_CONESTOGA',
  'STRAIGHT_TRUCK',
  'TANKER',
  'TANKER_ALUMINUM',
  'TANKER_CHEMICAL',
  'TANKER_CONTAINER',
  'TANKER_MULTI_COMPARTMENT',
  'TANKER_PNEUMATIC',
  'TANKER_STEEL',
  'VAN_CURTAIN_SIDE',
  'VAN_DOUBLE_PUP',
  'VAN_INSULATED',
  'VAN_OPEN_TOP',
  'VAN_OR_FLATBED',
  'VAN_OR_REEFER',
  'VAN_PLATED',
  'VAN_TRIPLE_PUP',
  'VAN_VENTED',

  'OTHER',
] as const;
export const makeStandardEquipmentTypeSchema = () =>
  z.enum(STANDARD_EQUIPMENT_TYPES);
const StandardEquipmentTypeSchema = makeStandardEquipmentTypeSchema();
export type StandardFreightOrderEquipmentType = z.infer<
  typeof StandardEquipmentTypeSchema
>;

export const STANDARD_DRIVER_REQUIREMENTS = [
  'NO_PREFERENCE',
  'SOLO',
  'TEAM',
] as const;
export const makeStandardDriverRequirementSchema = () =>
  z.enum(STANDARD_DRIVER_REQUIREMENTS);
const StandardDriverRequirementSchema = makeStandardDriverRequirementSchema();
export type StandardFreightOrderDriverRequirement = z.infer<
  typeof StandardDriverRequirementSchema
>;

export const AUCTION_TYPES = ['AUCTION', 'NON_AUCTION'] as const;
type AuctionTuple = typeof AUCTION_TYPES;
export type AuctionType = AuctionTuple[number];

export const CONTRACT_TYPES = ['CONTRACT', 'NON_CONTRACT'] as const;
type ContractTuple = typeof CONTRACT_TYPES;
export type ContractType = ContractTuple[number];

export const STANDARD_TRANSACTION_PURPOSES = [
  'ORIGINAL',
  'UPDATE',
  'CANCELLATION',
] as const;
type StandardTransactionPurposeTuple = typeof STANDARD_TRANSACTION_PURPOSES;
export type StandardTransactionPurpose =
  StandardTransactionPurposeTuple[number];

export const STANDARD_SHIPPING_UNITS = [
  'BAGS',
  'BALES',
  'BOTTLES',
  'BOXES',
  'BUNDLES',
  'CARTONS',
  'CASES',
  'CRATES',
  'DRUMS',
  'GALLONS',
  'PACKAGES',
  'PAILS',
  'PALLETS',
  'PIECES',
  'ROLLS',
  'SKIDS',
  'TRUCKLOADS',
  'TUBES',
  'UNITS',
  'VEHICLES',
] as const;
export const makeStandardShippingUnitSchema = () =>
  z.enum(STANDARD_SHIPPING_UNITS);
const StandardShippingUnitSchema = makeStandardShippingUnitSchema();
export type StandardShippingUnit = z.infer<typeof StandardShippingUnitSchema>;

export const STANDARD_FREIGHT_MODES = [
  'TRUCKLOAD',
  'DRAYAGE',
  'PARTIAL_TRUCKLOAD',
  'LTL',
  'PARCEL',
] as const;
export const makeStandardFreightModeSchema = () =>
  z.enum(STANDARD_FREIGHT_MODES);
const StandardFreightModeSchema = makeStandardFreightModeSchema();
export type StandardFreightMode = z.infer<typeof StandardFreightModeSchema>;

export const DRAYAGE_SUBTYPES = ['IMPORT', 'EXPORT'] as const;
type DrayageSubtypeTuple = typeof DRAYAGE_SUBTYPES;
export type DrayageSubtype = DrayageSubtypeTuple[number];

export const FACILITY_TYPES = [
  'COMMERCIAL_ADDRESS',
  'SEAPORT',
  'AIRPORT',
  'RAIL_YARD',
  'DEPOT',
] as const;
export const makeFacilityTypeSchema = () => z.enum(FACILITY_TYPES);
const FacilityTypeSchema = makeFacilityTypeSchema();
export type FacilityType = z.infer<typeof FacilityTypeSchema>;

export const STANDARD_LINE_ITEMS = [
  'FREIGHT_FLAT',
  'FREIGHT_PER_MILE',
  'PER_UNIT_WEIGHT',
  // TODO(max): FUEL is used for EDI. Usages of FUEL_FLAT should be converted over to FUEL in the future (legacy from Turvo names).
  'FUEL',
  'FUEL_FLAT',
  'FUEL_PER_MILE',
  'FREIGHT_PER_CONTAINER',
  'CHASSIS',
  'OVERWEIGHT',
  'PRE_PULL',
  'STORAGE',
  'DETENTION',
  'TMF_PIER_PASS',
  'PORT_FEE',
  'REDELIVERY',
  'EXTRA_MILES',
  'TRANSLOAD',
  'ESCORT',
  'EXPEDITED',
  'CHASSIS_SPLIT',
  'OTHER',
] as const;
export const makeStandardLineItemTypeSchema = () => z.enum(STANDARD_LINE_ITEMS);
const StandardLineItemTypeSchema = makeStandardLineItemTypeSchema();
export type StandardLineItemType = z.infer<typeof StandardLineItemTypeSchema>;

export const STANDARD_ACCESSORIALS = [
  'LIFTGATE',
  'TARPING',
  'TARPS_4FT',
  'TARPS_6FT',
  'TARPS_8FT',
  'TARPS_10FT',
  'HEADACHE_RACK',
  'COIL_RACK',
  'PIPE_STAKES',
  'GPS',
  'OVER_DIMENSION',
  'NON_ROUTED_SHIPMENT',
  'DELIVERY_BY_APPOINTMENT',
  'BLANKET_WRAP',
  'BLIND_SHIPMENT',
  'DRIVER_ASSISTANCE',
  'SCALE',
  'TEMPERATURE_CONTROLLED',
  'STRAPS',
  'LOAD_BARS',
  'PALLET_JACK',
  'HAZMAT',
  'PAPS_FAST',
  'TEAM',
  'TANKER_ENDORSEMENT',
  'DRAYAGE',
  'GUARANTEED_DELIVERY',
  'TRANSLOADING',
  'PPE',
  'CHAINS_AND_BINDERS',
  'DUNNAGE',
  'BACKHAUL',
  'DOCK_HEIGHT',
  'EDGE_PROTECTORS',
  'FOOD_GRADE',
  'TWIC',
  'LIFTGATE_DELIVERY',
  'LIFTGATE_PICKUP',
  'INSIDE_PICKUP',
  'INSIDE_DELIVERY',
  'LIMITED_ACCESS',
  'NOTIFY_SHIPPER',
  'NOTIFY_CONSIGNEE',
  'PERMIT',
  'EXCLUSIVE_USE_TRAILER',
  'MILITARY_INSTALLATION',
  'DROP_TRAILER',
  'PALLET_EXCHANGE',
  'MARKET_VOLATILITY_SURCHARGE',
] as const;
export const makeStandardAccessorialSchema = () =>
  z.enum(STANDARD_ACCESSORIALS);
const StandardAccessorialSchema = makeStandardAccessorialSchema();
export type StandardAccessorial = z.infer<typeof StandardAccessorialSchema>;

export const FREIGHT_CLASSES = [
  '50',
  '55',
  '60',
  '65',
  '70',
  '77',
  '77.5',
  '85',
  '92',
  '92.5',
  '100',
  '110',
  '125',
  '150',
  '175',
  '200',
  '250',
  '300',
  '400',
  '500',
] as const;
type FreightClassTuple = typeof FREIGHT_CLASSES;
export type FreightClass = FreightClassTuple[number];

export const STANDARD_MILEAGE_UNITS = ['MI', 'KM'] as const;
type StandardMileageUnitsTuple = typeof STANDARD_MILEAGE_UNITS;
export type StandardMileageUnit = StandardMileageUnitsTuple[number];

export const STANDARD_DIMENSION_UNITS = ['IN', 'CM', 'FT'] as const;
export const makeStandardDimensionUnitSchema = () =>
  z.enum(STANDARD_DIMENSION_UNITS);
const StandardDimensionUnitSchema = makeStandardDimensionUnitSchema();
export type StandardDimensionUnit = z.infer<typeof StandardDimensionUnitSchema>;

export const STANDARD_TEMPERATURE_UNITS = ['F', 'C'] as const;
export const makeStandardTemperatureUnitSchema = () =>
  z.enum(STANDARD_TEMPERATURE_UNITS);
const StandardTemperatureUnitSchema = makeStandardTemperatureUnitSchema();
export type StandardTemperatureUnit = z.infer<
  typeof StandardTemperatureUnitSchema
>;

export const STANDARD_WEIGHT_UNITS = ['LBS', 'KGS'] as const;
export const makeStandardWeightUnitSchema = () => z.enum(STANDARD_WEIGHT_UNITS);
const StandardWeightUnitSchema = makeStandardWeightUnitSchema();
export type StandardWeightUnit = z.infer<typeof StandardWeightUnitSchema>;

export const STOP_TYPES = ['PICKUP', 'DELIVERY'] as const;
export const makeStopTypeSchema = () => z.enum(STOP_TYPES);
const stopTypeSchema = makeStopTypeSchema();
export type StopType = z.infer<typeof stopTypeSchema>;

export const STANDARD_TIME_RANGE_TYPES = [
  'FACILITY_HOURS',
  'APPOINTMENT',
] as const;
export const makeStandardTimeRangeTypeSchema = () =>
  z.enum(STANDARD_TIME_RANGE_TYPES);
const standardTimeRangeTypeSchema = makeStandardTimeRangeTypeSchema();
export type StandardTimeRangeType = z.infer<typeof standardTimeRangeTypeSchema>;

export const STANDARD_PAYMENT_TERMS = [
  'PREPAID',
  'COLLECT',
  'THIRD_PARTY',
] as const;
type StandardPaymentTermsTuple = typeof STANDARD_PAYMENT_TERMS;
export type StandardPaymentTerms = StandardPaymentTermsTuple[number];

export const STANDARD_CONTACT_ROLES = [
  'ACCOUNT_LEAD',
  'MANAGER',
  'DISPATCHER',
  'DRIVER',
  'BILLING',
  'CLAIMS',
  'OTHER',
] as const;
type StandardContactRoleTuple = typeof STANDARD_CONTACT_ROLES;
export type StandardContactRole = StandardContactRoleTuple[number];

export const HAZMAT_PACKING_GROUPS = ['I', 'II', 'III'] as const;
export const makeHazmatPackingGroupSchema = () => z.enum(HAZMAT_PACKING_GROUPS);
const hazmatPackingGroupSchema = makeHazmatPackingGroupSchema();
export type HazmatPackingGroup = z.infer<typeof hazmatPackingGroupSchema>;

export const HAZMAT_CLASSES = [
  '1',
  '1.1',
  '1.2',
  '1.3',
  '1.4',
  '1.5',
  '1.6',
  '2',
  '2.1',
  '2.2',
  '2.3',
  '3',
  '4',
  '4.1',
  '4.2',
  '4.3',
  '5',
  '5.1',
  '5.2',
  '6',
  '6.1',
  '6.2',
  '7',
  '8',
  '9',
] as const;
export const makeHazmatClassSchema = () => z.enum(HAZMAT_CLASSES);
const hazmatClassSchema = makeHazmatClassSchema();
export type HazmatClass = z.infer<typeof hazmatClassSchema>;

export const HAZMAT_CLASS_QUALIFIERS = ['P', 'S'] as const;
export const makeHazmatClassQualifierSchema = () =>
  z.enum(HAZMAT_CLASS_QUALIFIERS);
const hazmatClassQualifierSchema = makeHazmatClassQualifierSchema();
export type HazmatClassQualifier = z.infer<typeof hazmatClassQualifierSchema>;

export const DECIDE_IGNORE_MESSAGE_CLASSES = ['ATTEND', 'IGNORE'];
type DecideIgnoreMessageClassTuple = typeof DECIDE_IGNORE_MESSAGE_CLASSES;
export type DecideIgnoreMessageClass = DecideIgnoreMessageClassTuple[number];

export const DECIDE_MESSAGE_ACTION_CLASSES = [
  'IGNORE',
  // Umbrella class for freight-related workflows that may be supported by
  // Vooma products in the future, but are currently not.
  'NOT_CURRENTLY_SUPPORTED',
  'BUILD_QUOTE',
  'BUILD_ORDER',
  'BUILD_QUOTE_AND_ORDER',
  'UPDATE_ENTITIES',
];
type DecideMessageActionClassTuple = typeof DECIDE_MESSAGE_ACTION_CLASSES;
export type DecideMessageActionClass = DecideMessageActionClassTuple[number];

export const FREIGHT_ENTITY_TYPES = [
  // Spot quote lifecycle
  'SPOT_QUOTE_REQUEST',
  'SPOT_QUOTE_ACCEPTANCE',
  'SPOT_QUOTE_REJECTION',
  'SPOT_QUOTE_UPDATE',
  'SPOT_QUOTE_NEGOTIATION',

  // Contract rate lifecycle
  'CONTRACT_RATE_REQUEST',
  'CONTRACT_RATE_NEGOTIATION',
  'CONTRACT_RATE_ACCEPTANCE',
  'CONTRACT_RATE_REJECTION',

  // Auction lifecycle
  'NEW_AUCTION',
  'AUCTION_WON',
  'AUCTION_LOST',
  'AUCTION_DETAILS_UPDATED',

  // Quote responses
  'REPLY_TO_QUOTE_REQUEST',
  'REQUEST_FOR_DETAILS',

  // Order lifecycle
  'NEW_ORDER',
  'ORDER_UPDATE',
  'ORDER_CANCELLATION',

  // Appointment lifecycle
  'APPOINTMENT_CONFIRMED',
  'APPOINTMENT_UPDATE',
  'APPOINTMENT_CANCELLATION',
  'REQUEST_FOR_APPOINTMENT',

  // Carrier booking lifecycle
  'CARRIER_BOOKING_REQUEST',
  'CARRIER_BOOKING_CONFIRMED',
  'CARRIER_BOOKING_EXCEPTION',

  // Carrier availability notices
  'TRUCK_AVAILABILITY_NOTICE',

  // Shipment management lifecycle
  'LOAD_LOCATION_INQUIRY',
  'LOAD_ETA_INQUIRY',
  'LOAD_EXCEPTION',
  'LOAD_STATUS_UPDATE',
  'LOAD_DETAILS_REQUEST',
  'LOAD_DETAILS_RESPONSE',
  'CARRIER_LOAD_INQUIRY',
  'CARRIER_INQUIRY_RESPONSE',

  // Driver management lifecycle
  'DRIVER_LOCATION_UPDATE',
  'DRIVER_AVAILABILITY_UPDATE',
  'DRIVER_LOAD_INQUIRY',
  'DRIVER_EXCEPTION',

  // Invoice lifecycle
  'INVOICE_REQUEST',
  'INVOICE_SENT',
  'INVOICE_RECEIVED',
  'INVOICE_CANCELLATION',
  'INVOICE_EXCEPTION',

  // General communication.
  'ACKNOWLEDGEMENT',

  // Additional exception management.
  'OUT_OF_OFFICE',

  // Other
  'UNKNOWN',
  // For when it's clear it's a new general type of class, but the class isn't
  // included yet in the labeling tool.
  'MISSING_CLASS',
  // For when there's not enough information in the current labeling tool to
  // make a determination on what the class should be.
  'NOT_ENOUGH_INFORMATION',
  // For when the message is unrelated to Vooma's current or future product offerings.
  'NOT_APPLICABLE',
] as const;
type FreightEntityTypesTuple = typeof FREIGHT_ENTITY_TYPES;
export type FreightEntityType = FreightEntityTypesTuple[number];

export const EmailTriggerSpecialClass = {
  ANY: '*',
  NO_CLASSIFICATIONS: 'NO_CLASSIFICATIONS',
} as const;

export type EmailTriggerSpecialClass =
  (typeof EmailTriggerSpecialClass)[keyof typeof EmailTriggerSpecialClass];

export type EmailTriggerClass = FreightEntityType | EmailTriggerSpecialClass;

export const FREIGHT_ENTITY_MODES = ['LTL', 'FTL'];
type FreightEntityModesTuple = typeof FREIGHT_ENTITY_MODES;
export type FreightEntityMode = FreightEntityModesTuple[number];

export const FREIGHT_MESSAGE_SENDER_TYPES = ['AUTOMATED', 'HUMAN'];
type FreightMessageSenderTypesTuple = typeof FREIGHT_MESSAGE_SENDER_TYPES;
export type FreightMessageSenderType = FreightMessageSenderTypesTuple[number];

export const QUOTE_RESPONSE_CLASSES: FreightEntityType[] = [
  'REPLY_TO_QUOTE_REQUEST',
  'REQUEST_FOR_DETAILS',
];

export const STOP_SCHEDULING_TYPES = [
  'FIRST_COME_FIRST_SERVED',
  'APPOINTMENT',
] as const;
type StopSchedulingTypesTuple = typeof STOP_SCHEDULING_TYPES;
export type StopSchedulingType = StopSchedulingTypesTuple[number];

export const DAY_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;
type DayOfWeekTuple = typeof DAY_OF_WEEK;
export type DayOfWeek = DayOfWeekTuple[number];

const KNOWN_STRINGS: Record<string, string> = {
  ARCBEST_TL_QUOTING: 'AIMS',
  AVRL: 'AVRL',
  DAT: 'DAT',
  GPS: 'GPS',
  LTL: 'LTL',
  API_KEY: 'API Key',
  GREENSCREENS: 'Triumph',
  SOP_ONLY: 'SOP Only',
  SOP_AND_SIDE_EFFECTS: 'SOP and Side Effects',
  OPENAI: 'OpenAI',
  LLM: 'LLM',
  STT: 'STT',
  TTS: 'TTS',
  HOSTED_MCLEOD: 'Hosted McLeod',
  MCLEOD: 'McLeod',
};

export function enumToString(e: string) {
  if (KNOWN_STRINGS[e]) {
    return KNOWN_STRINGS[e];
  }
  const spaced = e.replace(/_/g, ' ');
  const words = spaced.split(' ');
  const capitalized = words.map((w) => capitalize(w));
  return capitalized.join(' ');
}

export enum SchedulePortalType {
  BLUE_YONDER = 'BLUE_YONDER',
  COSTCO = 'COSTCO',
  DSDLINK = 'DSDLINK',
  E2OPEN = 'E2OPEN',
  LMCONNECT = 'LMCONNECT',
  MANHATTAN = 'MANHATTAN',
  NCR = 'NCR',
  OPENDOCK = 'OPENDOCK',
  RYDERSHARE = 'RYDERSHARE',
  YARDVIEW = 'YARDVIEW',
}
export const makeSchedulePortalIntegrationMethodSchema = () =>
  z.nativeEnum(SchedulePortalIntegrationMethod);

export enum SchedulePortalIntegrationMethod {
  API = 'API',
  WEB = 'WEB',
}

export enum ScheduleMethod {
  EMAIL = 'EMAIL',
  PORTAL = 'PORTAL',
  PHONE = 'PHONE',

  // TODO(mike): Remove these. They are Portal types, not Schedule methods.
  BLUE_YONDER = 'BLUE_YONDER',
  COSTCO = 'COSTCO',
  DSDLINK = 'DSDLINK',
  E2OPEN = 'E2OPEN',
  LMCONNECT = 'LMCONNECT',
  MANHATTAN = 'MANHATTAN',
  NCR = 'NCR',
  OPENDOCK = 'OPENDOCK',
  RYDERSHARE = 'RYDERSHARE',
  YARDVIEW = 'YARDVIEW',
}
export const makeScheduleMethodSchema = () => z.nativeEnum(ScheduleMethod);

export enum ScheduleMethodCategory {
  SOP = 'SOP',
  API_PORTAL = 'API_PORTAL',
  WEB_PORTAL = 'WEB_PORTAL',
  UNKNOWN = 'UNKNOWN',
}

export enum PortalDatePolicy {
  USE_PORTAL_DATE = 'USE_PORTAL_DATE',
  USE_SPECIFIED_DATE = 'USE_SPECIFIED_DATE',
  UNKNOWN = 'UNKNOWN',
}

export enum AppointmentStatus {
  NEW = 'NEW',
  PENDING_BATCH_REQUEST = 'PENDING_BATCH_REQUEST',
  REQUESTED = 'REQUESTED',
  CONFIRMED = 'CONFIRMED',
  ESCALATED = 'ESCALATED',
  ABORTED = 'ABORTED',
}

export enum RichTextDocumentType {
  EMAIL_SIGNATURE = 'EmailSignature',
}

export enum RichTextDocumentStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export enum ThirdPartyTMSIntegrationTypes {
  ALJEX = 'ALJEX',
  DAT = 'DAT',
  HOSTED_MCLEOD = 'HOSTED_MCLEOD',
  INTEGRATIONLESS = 'INTEGRATIONLESS',
  LEGACY_MERCURYGATE = 'LEGACY_MERCURYGATE',
  LOADSMART = 'LOADSMART',
  LOADSTOP = 'LOADSTOP',
  MCLEOD = 'MCLEOD',
  MERCURYGATE = 'MERCURYGATE',
  MYCARRIERPORTAL = 'MYCARRIERPORTAL',
  REVENOVA = 'REVENOVA',
  STRATEGY_LIVE = 'STRATEGY_LIVE',
  TAI = 'TAI',
  TEST = 'TEST',
  THREE_PL = 'THREE_PL',
  TRUCKER_TOOLS = 'TRUCKER_TOOLS',
  TURVO = 'TURVO',
}

export enum IntegrationsWithDefaultConfigs {
  AVRL = 'AVRL',
  BITFREIGHTER = 'BITFREIGHTER',
  DAT = 'DAT',
  DHL_FREIGHT = 'DHL_FREIGHT',
  DRAYRATES_RATING = 'DRAYRATES_RATING',
  GREENSCREENS = 'GREENSCREENS',
  TABI = 'TABI',
  TRANSFIX = 'TRANSFIX',
  TRUCKSTOP = 'TRUCKSTOP',
  SONAR = 'SONAR',
  TAI = 'TAI',
}

export const VERIFICATION_SUPPORTED_INTEGRATION_TYPES = [
  'ALJEX',
  'ALVYS',
  'HOSTED_MCLEOD',
  'REVENOVA',
  'TAI',
  'THREE_PL',
  'TURVO',
  'TRANSFIX',
] as const;
